import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import Seo from "../components/Seo";
import { isPrerendering } from "../utils/isPrerendering";
import { apiUrl } from "../utils/apiBase";
import { blobToBase64 } from "../utils/blobToBase64";
import { buildSignedVisitorWaiverPdf } from "../utils/visitorWaiverPdf";

const WAIVER_PDF_PATH = "/docs/visitor/MAG_Visitor_Liability_Waiver.pdf";

const inputClass = "field-input";

function waiverAbsoluteUrl() {
  if (typeof window === "undefined") return WAIVER_PDF_PATH;
  return `${window.location.origin}${WAIVER_PDF_PATH}`;
}

function canvasToPngBytes(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not read signature."));
          return;
        }
        blob.arrayBuffer().then((ab) => resolve(new Uint8Array(ab)));
      },
      "image/png",
      0.92
    );
  });
}

export default function VisitorWaiver() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    hostName: "",
    visitDate: "",
    agreedToWaiver: false,
  });
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [mounted, setMounted] = useState(false);

  const canvasRef = useRef(null);
  const recaptchaRef = useRef(null);
  const drawing = useRef(false);
  const inkMoves = useRef(0);
  const lastPoint = useRef({ x: 0, y: 0 });

  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const w = Math.max(rect.width, 300);
    const h = Math.max(rect.height, 140);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 2.25;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    inkMoves.current = 0;
  };

  useLayoutEffect(() => {
    if (isPrerendering()) return;
    setupCanvas();
  }, []);

  useEffect(() => {
    if (isPrerendering()) return;
    setMounted(true);
  }, []);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const handlePointerDown = (e) => {
    if (!canvasRef.current) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const p = pointerPos(e);
    lastPoint.current = p;
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const handlePointerMove = (e) => {
    if (!drawing.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const p = pointerPos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    lastPoint.current = p;
    inkMoves.current += 1;
  };

  const endStroke = () => {
    drawing.current = false;
  };

  const clearSignature = () => {
    setupCanvas();
  };

  const handleChange = (e) => {
    const { id, type, checked, value } = e.target;
    const key = id;
    setFormData((prev) => ({
      ...prev,
      [key]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreedToWaiver) {
      setStatus("Please confirm you have read and agree to the visitor waiver.");
      return;
    }
    if (inkMoves.current < 12) {
      setStatus("Please sign in the signature box using your mouse, finger, or stylus.");
      return;
    }
    if (!captchaToken) {
      setStatus("Please complete the captcha.");
      return;
    }

    setIsSubmitting(true);
    setStatus("Preparing your signed PDF...");

    let pdfBlob;
    try {
      const sigBytes = await canvasToPngBytes(canvasRef.current);
      pdfBlob = await buildSignedVisitorWaiverPdf({
        waiverUrl: waiverAbsoluteUrl(),
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        hostName: formData.hostName.trim(),
        visitDate: formData.visitDate.trim(),
        signaturePngBytes: sigBytes,
      });
    } catch (err) {
      console.error(err);
      setStatus(
        err?.message?.includes("waiver PDF")
          ? err.message
          : "Could not prepare your signed PDF. Please try again or use Open / download PDF and contact your MAG host."
      );
      setIsSubmitting(false);
      return;
    }

    let signedPdfBase64;
    try {
      signedPdfBase64 = await blobToBase64(pdfBlob);
    } catch (err) {
      console.error(err);
      setStatus("Could not encode the PDF. Please try again.");
      setIsSubmitting(false);
      return;
    }

    const safeName = formData.name.trim().replace(/\s+/g, "-").slice(0, 80) || "visitor";
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      company: formData.company.trim(),
      hostName: formData.hostName.trim(),
      visitDate: formData.visitDate.trim(),
      agreedToWaiver: true,
      captcha: captchaToken,
      signedPdfBase64,
      signedPdfFilename: `MAG-visitor-waiver-signed-${safeName}.pdf`,
    };

    setStatus("Submitting...");

    try {
      const res = await fetch(apiUrl("/api/visitor-check-in"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setStatus("Thank you. Your check-in was submitted. Please follow instructions from your MAG host.");
        setFormData({
          name: "",
          email: "",
          company: "",
          hostName: "",
          visitDate: "",
          agreedToWaiver: false,
        });
        setCaptchaToken("");
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setupCanvas();
      } else {
        const err = await res.json().catch(() => ({}));
        setStatus(err.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      console.error(err);
      const msg = err && typeof err.message === "string" ? err.message : "";
      const isNetwork =
        msg === "Failed to fetch" ||
        msg === "Load failed" ||
        /networkerror|network request failed/i.test(msg);
      if (isNetwork) {
        setStatus(
          "Could not reach the server. For local testing: run the backend on port 5001, leave REACT_APP_API_BASE blank in frontend/.env (uses package.json proxy), then restart npm start."
        );
      } else {
        setStatus("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusTone =
    status.includes("Thank you") || status.includes("submitted")
      ? "text-green-600"
      : status.includes("failed") ||
          status.includes("error") ||
          status.includes("not configured") ||
          status.includes("Could not reach") ||
          status.includes("Please confirm") ||
          status.includes("Please sign") ||
          status.includes("Please complete the captcha") ||
          status.includes("Captcha") ||
          status.includes("Could not prepare") ||
          status.includes("Could not load")
        ? "text-red-600"
        : "text-gray-600";

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        noindex
        title="Visitor Check-In & Liability Waiver | MAG"
        description="MAG visitor check-in: review the liability waiver and sign on this page before your visit."
        path="/docs/visitor-waiver"
      />
      <section className="relative overflow-hidden bg-gray-950 px-4 py-16 text-white sm:py-20">
        <div className="absolute inset-0 bg-tech-grid opacity-30" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl">
          <p className="page-eyebrow-on-dark">Visitors</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl">
            Visitor check-in & liability waiver
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/75 sm:text-lg">
            Read the waiver below, fill in your details, sign in the box, and submit. Your signature is
            added to the official PDF automatically, no separate upload.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10 space-y-10">
        <section
          id="waiver-document"
          className="border border-gray-200 bg-white p-6 sm:p-8 lg:p-10"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Visitor liability waiver (read on site)
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Scroll the document here, or open it in a new tab if you prefer.
              </p>
            </div>
            <a
              href={WAIVER_PDF_PATH}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-dark shrink-0"
            >
              Open / download PDF
            </a>
          </div>
          <div className="overflow-hidden border border-gray-200">
            <iframe
              title="MAG Visitor Liability Waiver PDF"
              src={WAIVER_PDF_PATH}
              className="w-full min-h-[55vh] sm:min-h-[65vh]"
            />
          </div>
        </section>

        <section className="border border-gray-200 bg-white p-6 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Visitor check-in</h2>
          <p className="text-gray-600 mb-6">
            Complete the fields, draw your signature, and submit. A PDF is generated that includes the
            full waiver plus a signature record page.
          </p>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="sm:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full name (print)
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="name"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                Company / organization
              </label>
              <input
                id="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                autoComplete="organization"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="hostName" className="block text-sm font-medium text-gray-700 mb-1">
                MAG host (employee name)
              </label>
              <input
                id="hostName"
                type="text"
                value={formData.hostName}
                onChange={handleChange}
                placeholder="Who invited you?"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2 sm:max-w-xs">
              <label htmlFor="visitDate" className="block text-sm font-medium text-gray-700 mb-1">
                Visit date (optional)
              </label>
              <input
                id="visitDate"
                type="date"
                value={formData.visitDate}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            <div className="sm:col-span-2">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                <label htmlFor="sig-canvas" className="block text-sm font-medium text-gray-700">
                  Sign here <span className="text-red-600">*</span>
                </label>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 underline self-start sm:self-auto"
                >
                  Clear signature
                </button>
              </div>
              <div className="max-w-lg overflow-hidden border-2 border-gray-300 bg-gray-50 touch-none">
                <canvas
                  id="sig-canvas"
                  ref={canvasRef}
                  className="block w-full cursor-crosshair"
                  style={{ minHeight: "140px" }}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={endStroke}
                  onPointerCancel={endStroke}
                  onPointerLeave={endStroke}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Use mouse, finger, or stylus. Your drawing is embedded into the PDF you submit.
              </p>
            </div>

            <div className="border border-gray-200 bg-gray-50 p-4 sm:col-span-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  id="agreedToWaiver"
                  type="checkbox"
                  checked={formData.agreedToWaiver}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                <span className="text-sm text-gray-800 leading-relaxed">
                  I have read the{" "}
                  <a
                    href={WAIVER_PDF_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-gray-900 underline"
                  >
                    Visitor Liability Waiver & Terms of Entry
                  </a>{" "}
                  shown above and agree to be legally bound by it. I confirm the information I provide is
                  accurate and that my signature in the box is my own.
                </span>
              </label>
            </div>

            {mounted && process.env.REACT_APP_RECAPTCHA_SITE_KEY ? (
              <div className="sm:col-span-2">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  theme="light"
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token || "")}
                />
              </div>
            ) : null}

            <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary-dark w-full disabled:opacity-60 sm:w-auto"
              >
                {isSubmitting ? "Working..." : "Submit check-in"}
              </button>
              {status && <span className={`text-sm font-medium ${statusTone}`}>{status}</span>}
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
